/**
 * Avatar Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 343-105
 *
 * A flexible avatar component with support for:
 * - Four sizes: Extra Large, Large, Medium, Small
 * - Two types: Initial (letter) and Image
 * - Two shapes: Square and Rounded
 * - Two modes: Light and Dark
 */

import React from 'react';
import { View, Text, Image, StyleSheet, ImageSourcePropType } from 'react-native';
import { colors } from '../tokens/colors';

export interface AvatarProps {
  /**
   * Avatar size
   * @default "large"
   */
  size?: 'extraLarge' | 'large' | 'medium' | 'small';

  /**
   * Avatar type
   * @default "initial"
   */
  type?: 'initial' | 'image';

  /**
   * Avatar shape
   * @default "square"
   */
  shape?: 'square' | 'rounded';

  /**
   * Visual mode
   * @default "light"
   */
  mode?: 'light' | 'dark';

  /**
   * Initial letter(s) to display (for type="initial")
   * @default "A"
   */
  text?: string;

  /**
   * Image source (for type="image")
   */
  imageSource?: ImageSourcePropType;

  /**
   * Image URL (alternative to imageSource)
   */
  imageUrl?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  size = 'large',
  type = 'initial',
  shape = 'square',
  mode = 'light',
  text = 'A',
  imageSource,
  imageUrl,
}) => {
  // Get dimensions based on size (exact values from Figma)
  const getDimensions = () => {
    switch (size) {
      case 'extraLarge':
        return { size: 48, fontSize: 40 };
      case 'large':
        return { size: 40, fontSize: 32 };
      case 'medium':
        return { size: 32, fontSize: 28 };
      case 'small':
      default:
        return { size: 16, fontSize: 14 };
    }
  };

  // Get background color using Crop Circle color tokens
  const getBackgroundColor = () => {
    if (mode === 'dark') {
      return colors.brand.black; // #020202
    }
    return colors.brand.white; // #FCFCFC
  };

  // Get text color using Crop Circle color tokens
  const getTextColor = () => {
    if (mode === 'dark') {
      return colors.brand.white; // #FCFCFC
    }
    return colors.brand.black; // #020202
  };

  // Get border radius based on shape
  const getBorderRadius = () => {
    if (shape === 'rounded') {
      return 999; // Fully rounded
    }
    return 2; // Square with slight rounding
  };

  const dimensions = getDimensions();
  const backgroundColor = getBackgroundColor();
  const textColor = getTextColor();
  const borderRadius = getBorderRadius();

  // Render image avatar
  if (type === 'image' && (imageSource || imageUrl)) {
    return (
      <View
        style={[
          styles.container,
          {
            width: dimensions.size,
            height: dimensions.size,
            borderRadius,
          },
        ]}
      >
        <Image
          source={imageSource || { uri: imageUrl }}
          style={[
            styles.image,
            {
              width: dimensions.size,
              height: dimensions.size,
              borderRadius,
            },
          ]}
          resizeMode="cover"
        />
      </View>
    );
  }

  // Render initial avatar
  return (
    <View
      style={[
        styles.container,
        {
          width: dimensions.size,
          height: dimensions.size,
          backgroundColor,
          borderRadius,
        },
      ]}
    >
      <Text
        style={[
          styles.text,
          {
            color: textColor,
            fontSize: dimensions.fontSize,
            lineHeight: dimensions.fontSize * 1.25,
          },
        ]}
      >
        {text.toUpperCase()}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  text: {
    fontFamily: 'Inter',
    fontWeight: '400',
    textAlign: 'center',
  },
  image: {
    width: '100%',
    height: '100%',
  },
});

Avatar.displayName = 'Avatar';
